import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { startCase } from 'lodash';
import type { Editor, Range } from '@tiptap/react';
import type { MentionColorVariant } from '../components/ui/tiptap/extensions';
import type {
  MentionGroup,
  MentionOptionDetail,
  MentionSubgroup,
} from '../components/ui/tiptap/extensions';
import type { ResourceMentionPayload } from './use-graph-metions';
import { useToolsAsync } from './use-tools-info';
import {
  PROVIDER_ICONS,
  useOpenToolModal,
} from '@cotera/client/v0/components/tool-connections';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { extractDescriptionSummary } from '@cotera/client/app/components/tools/utils';
import type { ToolContextKind, ToolListItem } from '../etc/api/typed-client';
import { toolRunsInContext } from './use-available-tools';
import { coco } from '@cotera/sdk/core';
import { ResourceType, coco as apiCoco } from '@cotera/api';
import { useCocoMentionResources } from '../v1/shell/app.model';
import { formatAgentKeyForDisplay } from './use-agents-display-names';
import { CocoInputMentionDialog } from './coco-input-mention-dialog';
import {
  RESOURCE_TYPE_COLOR_CONFIG,
  resourceColorMentionVariant,
} from '../v1/resources/type/resource-type-colors';
import type {
  CocoInputConstructPayload,
  CocoInputMentionData,
  CocoInputPrimitiveType,
  CocoInputResolvedPayload,
  CocoMentionOption,
} from './coco-mention-types';
import { isCocoInputConstructMention } from './coco-mention-types';

export const COCO_INPUT_CONSTRUCT_GROUP_ID = 'coco-input';

export const COCO_INPUT_CONSTRUCT_REFERENCE_ID = '__coco_input_construct__';

export type {
  CocoInputConstructPayload,
  CocoInputMentionData,
  CocoInputPrimitiveType,
  CocoInputResolvedPayload,
  CocoMentionData,
  CocoMentionOption,
} from './coco-mention-types';
export { isCocoInputConstructMention } from './coco-mention-types';

const getProviderFromToolKey = (toolKey: string): string => {
  const firstSlash = toolKey.indexOf('/');
  return firstSlash > 0 ? toolKey.substring(0, firstSlash) : toolKey;
};

const formatProviderLabel = (provider: string): string => {
  const specialCases: Record<string, string> = {
    github: 'GitHub',
    linkedin: 'LinkedIn',
    newsapi: 'NewsAPI',
    'google-sheets': 'Google Sheets',
    'google-jobs': 'Google Jobs',
    'fcp-euro': 'FCP Euro',
    'maps-data': 'Maps Data',
    'snowflake-cortex': 'Snowflake Cortex',
  };

  if (specialCases[provider.toLowerCase()] !== undefined) {
    return specialCases[provider.toLowerCase()]!;
  }

  return startCase(provider);
};

/** Provider brand icon for the Tools cascade providers column (undefined if unmapped). */
const renderProviderIcon = (provider: string): React.ReactNode => {
  const ProviderLogo = PROVIDER_ICONS[provider];
  if (ProviderLogo === undefined) {
    return undefined;
  }
  // Provider logos are wildly inconsistent: some render a raw <svg> with a
  // hard-coded height, others a fixed-size <div> wrapper or an <img>. Pin every
  // logo into a uniform box so the column aligns — but make the artwork *fit*
  // the box (fill it while preserving aspect ratio) rather than being forced to
  // an exact square and clipped. Using size-full (relative) instead of a fixed
  // !h-6/!w-6 also lets the logo scale down cleanly when a consumer (e.g. the
  // resource picker) renders this node in a smaller box, so nothing is cropped.
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center [&>*]:!size-full [&_svg]:!size-full [&_img]:object-contain">
      <ProviderLogo />
    </span>
  );
};

/**
 * Human-friendly access descriptor from a tool's read/write semantics, so users
 * see "Read-only" instead of "query" and know whether a tool changes anything.
 */
const toolAccess = (
  toolType: ToolListItem['toolType']
): MentionOptionDetail['access'] => {
  if (toolType === 'mutation') {
    return { label: 'Writes data', tone: 'write' };
  }
  if (toolType === 'interruptable') {
    return { label: 'Interactive', tone: 'write' };
  }
  return { label: 'Read-only', tone: 'read' };
};

/** Build the plain-language detail preview shown in the Tools cascade. */
const buildToolDetail = (
  tool: ToolListItem,
  openToolModal: (toolKey: string) => void
): MentionOptionDetail => ({
  eyebrow: `${tool.providerDisplayName} · ${tool.name}`,
  title: tool.name,
  summary: extractDescriptionSummary(
    tool.defaultDescription ?? tool.description
  ),
  access: toolAccess(tool.toolType),
  onOpen: () => openToolModal(tool.key),
});

const RESOURCE_TYPES_FOR_MENTIONS = [
  ResourceType.Agent,
  ResourceType.Document,
  ResourceType.Folder,
  ResourceType.Dataset,
] as const;

type MentionResourceType = (typeof RESOURCE_TYPES_FOR_MENTIONS)[number];

/**
 * A group the @ menu can offer, for {@link UseCocoMentionsConfig.resourceTypes}.
 * Beyond the resource types above, `workflow` and `tool` are backed by tools
 * rather than resource rows, but read as their own categories to the user.
 */
export type MentionResourceKind =
  | MentionResourceType
  | ResourceType.Workflow
  | 'tool';

export const ALL_MENTION_RESOURCE_KINDS: readonly MentionResourceKind[] = [
  ...RESOURCE_TYPES_FOR_MENTIONS,
  ResourceType.Workflow,
  'tool',
] as const;

const RESOURCE_GROUP_LABEL: Record<MentionResourceType, string> = {
  [ResourceType.Agent]: 'Agents',
  [ResourceType.Document]: 'Documents',
  [ResourceType.Folder]: 'Folders',
  [ResourceType.Dataset]: 'Datasets',
};

/** Singular label for @-mention rows (e.g. "foo · Agent"). */
const RESOURCE_TYPE_DROPDOWN_TYPE_LABEL: Record<MentionResourceType, string> = {
  [ResourceType.Agent]: 'Agent',
  [ResourceType.Document]: 'Document',
  [ResourceType.Folder]: 'Folder',
  [ResourceType.Dataset]: 'Dataset',
};

/**
 * Category icons for the top-level @ menu, matching the sidebar's icon set
 * (agents=bot, documents=document-text, folders=folder, datasets=database)
 * so the mention menu reads as part of the same product.
 */
const RESOURCE_GROUP_ICON: Record<MentionResourceType, React.ReactNode> = {
  [ResourceType.Agent]: <Icon icon="agent" />,
  [ResourceType.Document]: <Icon icon="document-text" />,
  [ResourceType.Folder]: <Icon icon="folder" />,
  [ResourceType.Dataset]: <Icon icon="dataset" />,
};

/** Category icon for the Tools group; matches the sidebar's "open tools" wrench. */
const TOOL_GROUP_ICON: React.ReactNode = <Icon icon="wrench" />;

/** Category icon for the Workflows group; matches the sidebar's workflows icon. */
const WORKFLOW_GROUP_ICON: React.ReactNode = <Icon icon="workflow" />;

const WORKFLOW_GROUP_ID = 'workflow';

const resourceGroupId = (type: MentionResourceType): string =>
  `resource-${type}`;

const resourceMentionColor = (
  type: MentionResourceType
): MentionColorVariant | undefined => {
  return resourceColorMentionVariant(
    RESOURCE_TYPE_COLOR_CONFIG[type].resourceColor
  );
};

const buildResourceDetail = (
  title: string,
  description: string | null,
  eyebrow: string
): MentionOptionDetail | undefined => {
  if (description === null) {
    return undefined;
  }
  const summary = extractDescriptionSummary(description).trim();
  if (summary === '') {
    return undefined;
  }
  return {
    eyebrow,
    title,
    summary,
  };
};

const isCocoAgentTool = (toolKey: string): boolean =>
  coco.isAgentToolKey(toolKey);

const isCocoWorkflowTool = (toolKey: string): boolean =>
  apiCoco.isWorkflowToolKey(toolKey);

const COCO_INPUT_CONSTRUCT_OPTION: CocoMentionOption = {
  label: 'Input',
  data: {
    t: 'input',
    name: COCO_INPUT_CONSTRUCT_REFERENCE_ID,
    data: { kind: 'construct' } satisfies CocoInputConstructPayload,
  },
  group: COCO_INPUT_CONSTRUCT_GROUP_ID,
  referenceId: COCO_INPUT_CONSTRUCT_REFERENCE_ID,
  providerLabel: 'Inputs',
  color: 'green',
};

const COCO_INPUT_GROUP: MentionGroup = {
  id: COCO_INPUT_CONSTRUCT_GROUP_ID,
  label: 'Inputs',
};

export type UseCocoMentionsConfig = {
  excludeAgentKey?: string;
  excludeDocumentId?: string;
  /**
   * @deprecated Agent tools are always excluded from the Tools @-mention group.
   * Agents are only listed under the Agents resource group.
   */
  excludeAgentTools?: boolean;
  /** When true, adds an @ menu entry to insert typed input mentions (agent/document editors only). */
  enableInputs?: boolean;
  /**
   * Renders the picker's own search box. Off by default: inside an editor the
   * `@` query already filters the menu, and a second focused input would fight
   * it for the caret. Pickers opened outside an editor (e.g. the workflow
   * add-node modal) have no such query, so they turn it on.
   */
  enableSearch?: boolean;
  /**
   * Which groups to offer. `'all'` (the default) offers every one. Pass a subset
   * to narrow the menu to what the surface can actually consume — the workflow
   * builder takes agents, workflows and tools, and has no use for folders.
   *
   * Hold the array at module scope: it keys the memo that builds every option,
   * so a fresh literal each render rebuilds the whole menu each render.
   */
  resourceTypes?: readonly MentionResourceKind[] | 'all';
  /**
   * Only offer tools that can run in this context. Omit for the full catalog;
   * pass `'workflow'` so chat-only tools (which throw in a ToolCall node) stay out.
   */
  toolContext?: ToolContextKind;
};

export type UseCocoMentionsResult = {
  options: CocoMentionOption[];
  groups: MentionGroup[];
  isLoading: boolean;
  /** Mirrors {@link UseCocoMentionsConfig.enableSearch}; forward to the picker. */
  enableSearch: boolean;
  /**
   * Pass to {@link MentionDropdownMenu}. When the user picks the synthetic "Input" row,
   * returns true after opening the name/type dialog (default mention insert is skipped).
   */
  interceptMentionInsert?: (props: {
    editor: Editor;
    range: Range;
    context?: CocoMentionOption;
  }) => boolean;
  /** Render next to the editor when `enableInputs` is true. */
  cocoInputMentionDialog?: React.ReactNode;
};

type PendingInputInsert = {
  editor: Editor;
  insertPos: number;
};

/**
 * Workspace @ mentions for Coco chat and rich agent/document editors: resources plus tools.
 * With {@link UseCocoMentionsConfig.enableInputs}, also surfaces a synthetic "Input" row and
 * companion dialog UI for defining typed input mentions in the editor.
 */
export const useCocoMentions = (
  config?: UseCocoMentionsConfig
): UseCocoMentionsResult => {
  const enableInputs = config?.enableInputs === true;
  const enableSearch = config?.enableSearch === true;
  const { tools, isLoading: toolsLoading } = useToolsAsync();
  const excludeAgentKey = config?.excludeAgentKey;
  const excludeDocumentId = config?.excludeDocumentId;
  const resourceTypes = config?.resourceTypes ?? 'all';
  const toolContext = config?.toolContext;
  const { resources: cocoMentionResources, isLoading: resourcesLoading } =
    useCocoMentionResources();
  // Deep-links a tool's "Open details" link to its connections modal. Stable
  // (memoized on the modal-manager actions), so it's safe in the memo below.
  const openToolModal = useOpenToolModal();
  const pendingInputInsertRef = useRef<PendingInputInsert | null>(null);
  const [cocoInputDialogOpen, setCocoInputDialogOpen] = useState(false);

  const isLoading = toolsLoading || resourcesLoading;

  const built = useMemo(() => {
    const toolProviders = new Set<string>();

    const includeKind = (kind: MentionResourceKind): boolean =>
      resourceTypes === 'all' || resourceTypes.includes(kind);

    /** The rows for `kind`, or none when the caller filtered that group out. */
    const groupRows = <T,>(
      kind: MentionResourceKind,
      rows: readonly T[]
    ): readonly T[] => (includeKind(kind) ? rows : []);

    const filteredTools = groupRows(
      'tool',
      (excludeAgentKey !== undefined
        ? tools.filter(
            (tool) =>
              !(
                tool.implementationType === 'agent' &&
                tool.key === `${coco.prefix}${excludeAgentKey}`
              )
          )
        : tools
      )
        .filter((tool) => isCocoAgentTool(tool.key) === false)
        .filter((tool) => isCocoWorkflowTool(tool.key) === false)
        // Deprecated tools (e.g. google/search) stay resolvable for agents that
        // already mention them, but should not be offered as new @-mentions.
        .filter((tool) => tool.hidden !== true)
        .filter((tool) => toolRunsInContext(tool, toolContext))
    );

    const toolMentionOptions: CocoMentionOption[] = filteredTools.map(
      (tool) => {
        const provider = getProviderFromToolKey(tool.key);
        toolProviders.add(provider);
        return {
          label: tool.name,
          data: {
            t: 'tool' as const,
            name: tool.key,
            data: {
              toolId: tool.key,
              toolName: tool.key,
            },
          },
          group: 'tool',
          subgroup: provider,
          referenceId: tool.key,
          providerLabel: formatProviderLabel(provider),
          typeLabel: 'Tool',
          // One consistent color for every tool. The dot encodes the category
          // (tool vs document vs agent…), not the tool's query/mutation subtype,
          // which is noise to the user.
          color: resourceColorMentionVariant('tool'),
          // Ready-to-use tools get a check in the cascade tools column.
          available: tool.canExecute,
          // Plain-language preview that abstracts away endpoint/schema details.
          detail: buildToolDetail(tool, openToolModal),
        };
      }
    );

    // Workflows are sourced from actual workflow resources (not the tool list),
    // so the Workflows group refreshes on workflow resource events — the same way
    // agents and documents do. Each workflow is inserted as its backing workflow
    // tool, whose key is derived deterministically from the workflow id.
    const workflowMentionOptions: CocoMentionOption[] = [];
    for (const workflow of groupRows(
      ResourceType.Workflow,
      cocoMentionResources.workflows
    )) {
      if (workflow.status !== 'active') {
        continue;
      }
      const nameTrim = workflow.name.trim();
      const label = nameTrim !== '' ? nameTrim : 'Untitled workflow';
      const toolKey = apiCoco.workflowIdToWorkflowToolKey(workflow.id);
      workflowMentionOptions.push({
        label,
        data: {
          t: 'tool' as const,
          name: toolKey,
          data: {
            toolId: toolKey,
            toolName: toolKey,
          },
        },
        group: WORKFLOW_GROUP_ID,
        referenceId: toolKey,
        providerLabel: 'Workflows',
        typeLabel: 'Workflow',
        color: 'teal',
      });
    }

    const providerSubgroups: MentionSubgroup[] = Array.from(toolProviders)
      .sort((a, b) =>
        formatProviderLabel(a).localeCompare(formatProviderLabel(b))
      )
      .map((provider) => ({
        id: provider,
        label: formatProviderLabel(provider),
        icon: renderProviderIcon(provider),
      }));

    const toolSubgroups: MentionSubgroup[] = [...providerSubgroups];

    const resourceMentionOptions: CocoMentionOption[] = [];

    for (const agent of groupRows(
      ResourceType.Agent,
      cocoMentionResources.agents
    )) {
      if (agent.status !== 'active') {
        continue;
      }
      if (excludeAgentKey !== undefined && agent.agentKey === excludeAgentKey) {
        continue;
      }
      const titleTrim =
        agent.title !== null && agent.title !== undefined
          ? agent.title.trim()
          : '';
      const label =
        titleTrim !== '' ? titleTrim : formatAgentKeyForDisplay(agent.agentKey);
      const payload: ResourceMentionPayload = {
        resourceType: ResourceType.Agent,
        resourceId: agent.id,
      };
      resourceMentionOptions.push({
        label,
        data: {
          t: 'resource',
          name: `${ResourceType.Agent}:${agent.id}`,
          data: payload,
        },
        group: resourceGroupId(ResourceType.Agent),
        referenceId: agent.id,
        providerLabel: RESOURCE_GROUP_LABEL[ResourceType.Agent],
        typeLabel: RESOURCE_TYPE_DROPDOWN_TYPE_LABEL[ResourceType.Agent],
        color: resourceMentionColor(ResourceType.Agent),
        detail: buildResourceDetail(
          label,
          agent.description,
          RESOURCE_TYPE_DROPDOWN_TYPE_LABEL[ResourceType.Agent]
        ),
      });
    }

    for (const doc of groupRows(
      ResourceType.Document,
      cocoMentionResources.documents
    )) {
      if (doc.status !== 'active') {
        continue;
      }
      if (excludeDocumentId !== undefined && doc.id === excludeDocumentId) {
        continue;
      }
      const label =
        doc.title !== null && doc.title.trim() !== ''
          ? doc.title.trim()
          : 'Untitled document';
      const payload: ResourceMentionPayload = {
        resourceType: ResourceType.Document,
        resourceId: doc.id,
      };
      resourceMentionOptions.push({
        label,
        data: {
          t: 'resource',
          name: `${ResourceType.Document}:${doc.id}`,
          data: payload,
        },
        group: resourceGroupId(ResourceType.Document),
        referenceId: doc.id,
        providerLabel: RESOURCE_GROUP_LABEL[ResourceType.Document],
        typeLabel: RESOURCE_TYPE_DROPDOWN_TYPE_LABEL[ResourceType.Document],
        color: resourceMentionColor(ResourceType.Document),
        detail: buildResourceDetail(
          label,
          doc.description,
          RESOURCE_TYPE_DROPDOWN_TYPE_LABEL[ResourceType.Document]
        ),
      });
    }

    for (const folder of groupRows(
      ResourceType.Folder,
      cocoMentionResources.folders
    )) {
      const nameTrim = folder.name.trim();
      const label = nameTrim !== '' ? nameTrim : 'Untitled folder';
      const payload: ResourceMentionPayload = {
        resourceType: ResourceType.Folder,
        resourceId: folder.id,
      };
      resourceMentionOptions.push({
        label,
        data: {
          t: 'resource',
          name: `${ResourceType.Folder}:${folder.id}`,
          data: payload,
        },
        group: resourceGroupId(ResourceType.Folder),
        referenceId: folder.id,
        providerLabel: RESOURCE_GROUP_LABEL[ResourceType.Folder],
        typeLabel: RESOURCE_TYPE_DROPDOWN_TYPE_LABEL[ResourceType.Folder],
        color: resourceMentionColor(ResourceType.Folder),
      });
    }

    for (const dataset of groupRows(
      ResourceType.Dataset,
      cocoMentionResources.datasets
    )) {
      if (dataset.status !== 'active') {
        continue;
      }
      const nameTrim = dataset.name.trim();
      const label = nameTrim !== '' ? nameTrim : 'Untitled dataset';
      const payload: ResourceMentionPayload = {
        resourceType: ResourceType.Dataset,
        resourceId: dataset.id,
      };
      resourceMentionOptions.push({
        label,
        data: {
          t: 'resource',
          name: `${ResourceType.Dataset}:${dataset.id}`,
          data: payload,
        },
        group: resourceGroupId(ResourceType.Dataset),
        referenceId: dataset.id,
        providerLabel: RESOURCE_GROUP_LABEL[ResourceType.Dataset],
        typeLabel: RESOURCE_TYPE_DROPDOWN_TYPE_LABEL[ResourceType.Dataset],
        color: resourceMentionColor(ResourceType.Dataset),
      });
    }

    const resourceGroups: MentionGroup[] = RESOURCE_TYPES_FOR_MENTIONS.filter(
      (rt) =>
        resourceMentionOptions.some((o) => o.group === resourceGroupId(rt))
    ).map((rt) => ({
      id: resourceGroupId(rt),
      label: RESOURCE_GROUP_LABEL[rt],
      icon: RESOURCE_GROUP_ICON[rt],
    }));

    const toolGroup: MentionGroup | null =
      toolMentionOptions.length > 0
        ? {
            id: 'tool',
            label: 'Tools',
            icon: TOOL_GROUP_ICON,
            subgroups: toolSubgroups.length > 0 ? toolSubgroups : undefined,
          }
        : null;

    const workflowGroup: MentionGroup | null =
      workflowMentionOptions.length > 0
        ? {
            id: WORKFLOW_GROUP_ID,
            label: 'Workflows',
            icon: WORKFLOW_GROUP_ICON,
          }
        : null;

    const groups: MentionGroup[] = [
      ...(enableInputs ? [COCO_INPUT_GROUP] : []),
      ...resourceGroups,
      ...(workflowGroup !== null ? [workflowGroup] : []),
      ...(toolGroup !== null ? [toolGroup] : []),
    ];

    const mentionOptions: CocoMentionOption[] = [
      ...(enableInputs ? [COCO_INPUT_CONSTRUCT_OPTION] : []),
      ...resourceMentionOptions,
      ...workflowMentionOptions,
      ...toolMentionOptions,
    ];

    return { mentionOptions, mentionGroups: groups };
  }, [
    tools,
    excludeAgentKey,
    excludeDocumentId,
    enableInputs,
    resourceTypes,
    toolContext,
    cocoMentionResources,
    openToolModal,
  ]);

  const handleConfirmInputMention = useCallback(
    (inputKey: string, inputType: CocoInputPrimitiveType) => {
      const pending = pendingInputInsertRef.current;
      pendingInputInsertRef.current = null;
      setCocoInputDialogOpen(false);
      if (pending === null || pending.editor.isDestroyed) {
        return;
      }
      const { editor, insertPos } = pending;
      const resolved: CocoInputResolvedPayload = { inputKey, inputType };
      const mentionData: CocoInputMentionData = {
        t: 'input',
        name: inputKey,
        data: resolved,
      };
      const label = `${inputKey}:${inputType}`;
      editor
        .chain()
        .focus()
        .insertContentAt(insertPos, [
          {
            type: 'mention',
            attrs: {
              id: `input:${inputKey}`,
              label,
              referenceId: inputKey,
              data: JSON.stringify(mentionData),
            },
          },
          {
            type: 'text',
            text: ' ',
          },
        ])
        .run();
    },
    []
  );

  const interceptMentionInsert = useCallback(
    (props: {
      editor: Editor;
      range: Range;
      context?: CocoMentionOption;
    }): boolean => {
      if (!enableInputs) {
        return false;
      }
      if (props.context === undefined) {
        return false;
      }
      if (!isCocoInputConstructMention(props.context.data)) {
        return false;
      }
      const insertPos = props.range.from;
      props.editor.chain().focus().deleteRange(props.range).run();
      pendingInputInsertRef.current = { editor: props.editor, insertPos };
      setCocoInputDialogOpen(true);
      return true;
    },
    [enableInputs]
  );

  const cocoInputMentionDialog = enableInputs ? (
    <CocoInputMentionDialog
      open={cocoInputDialogOpen}
      onOpenChange={(open) => {
        if (!open) {
          pendingInputInsertRef.current = null;
          setCocoInputDialogOpen(false);
        }
      }}
      onConfirm={handleConfirmInputMention}
    />
  ) : undefined;

  return {
    options: built.mentionOptions,
    groups: built.mentionGroups,
    isLoading,
    enableSearch,
    ...(enableInputs ? { interceptMentionInsert, cocoInputMentionDialog } : {}),
  };
};
